// Promotions list / approval queue. Daily-driver page.

import { ArrowRight, CheckCircle2, XCircle, Play } from 'lucide-react';
import { Card, CardHeader } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import {
  usePromotions,
  approvePromotion,
  rejectPromotion,
  executePromotion,
  type Promotion,
} from '../api/promotions';

function statusTone(s: Promotion['status']): 'success' | 'warning' | 'danger' | 'neutral' | 'accent' {
  switch (s) {
    case 'executed': return 'success';
    case 'approved': return 'accent';
    case 'pending': return 'warning';
    case 'rejected': return 'neutral';
    case 'failed': return 'danger';
  }
}

export function Promotions() {
  const { data, mutate } = usePromotions();

  const onApprove = async (id: string) => {
    await approvePromotion(id);
    await mutate();
  };
  const onReject = async (id: string) => {
    if (!confirm('Reject this promotion?')) return;
    await rejectPromotion(id);
    await mutate();
  };
  const onExecute = async (id: string) => {
    if (!confirm('Execute this promotion now? Artifacts will be copied to the destination repo.')) return;
    try {
      await executePromotion(id);
    } catch (e) {
      alert(`Execution failed: ${e}`);
    }
    await mutate();
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="orb-eyebrow text-accent/80 mb-2">Workflow</p>
        <h1 className="text-2xl font-display font-semibold text-fg leading-tight">Promotions</h1>
        <p className="text-fg-muted text-sm mt-2 max-w-2xl">
          Copy artifacts from one repo to another. The optional approval
          gate keeps a separation between "request" and "ship" — useful for
          staging → production lanes.
        </p>
      </div>

      <Card>
        <CardHeader title={`Recent promotions (${data?.length ?? 0})`} />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="px-5 py-3 font-medium text-fg-muted">Status</th>
                <th className="px-5 py-3 font-medium text-fg-muted">From → To</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Artifacts</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Requested</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Note</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {data?.length ? (
                data.map((p) => (
                  <tr key={p.id} className="border-b border-border/60 last:border-0 hover:bg-bg-hover/40">
                    <td className="px-5 py-3">
                      <Badge tone={statusTone(p.status)}>{p.status}</Badge>
                    </td>
                    <td className="px-5 py-3 text-fg flex items-center gap-2 text-xs">
                      <span className="orb-code">{p.src_slug}</span>
                      <ArrowRight className="h-3 w-3 text-fg-muted" />
                      <span className="orb-code">{p.dst_slug}</span>
                    </td>
                    <td className="px-5 py-3 text-fg-muted text-xs">{p.artifact_count}</td>
                    <td className="px-5 py-3 text-fg-muted text-xs">
                      {p.requested_by}
                      <br />
                      {new Date(p.requested_at).toLocaleString()}
                    </td>
                    <td className="px-5 py-3 text-fg-muted text-xs max-w-md truncate">
                      {p.note || '—'}
                      {p.error && <span className="block text-danger text-[10px]">{p.error}</span>}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {p.status === 'pending' && (
                          <>
                            <button
                              type="button"
                              onClick={() => onApprove(p.id)}
                              title="Approve"
                              className="h-8 w-8 inline-flex items-center justify-center rounded-md text-fg-muted hover:bg-success/10 hover:text-success"
                            >
                              <CheckCircle2 className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => onReject(p.id)}
                              title="Reject"
                              className="h-8 w-8 inline-flex items-center justify-center rounded-md text-fg-muted hover:bg-danger/10 hover:text-danger"
                            >
                              <XCircle className="h-4 w-4" />
                            </button>
                          </>
                        )}
                        {p.status === 'approved' && (
                          <button
                            type="button"
                            onClick={() => onExecute(p.id)}
                            title="Execute"
                            className="h-8 w-8 inline-flex items-center justify-center rounded-md text-fg-muted hover:bg-accent/10 hover:text-accent"
                          >
                            <Play className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center text-fg-muted text-sm">
                    No promotions yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
